/**
 * "Test stream" (ENG-2470): a button that opens the stream test for one
 * camera in a dialog. Used on the camera page and per camera in the
 * Troubleshooter. Shown only when the site's About says
 * `stream_test_available` (the test is switched on and the site is a video
 * site it supports), so the button never leads to a refusal.
 */
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { StreamTestPanel } from "./StreamTestPanel";

export const StreamTestDialog = ({
  open,
  onClose,
  cameraId,
  cameraName,
}: {
  open: boolean;
  onClose: () => void;
  cameraId: number;
  cameraName?: string | null;
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle
      sx={{ fontFamily: "Mulish", fontWeight: 800, color: "#001943", pr: 6 }}
    >
      Test stream
      {cameraName ? (
        <span className="block font-mulish font-normal text-[14px] text-[#4c5257]">
          {cameraName}
        </span>
      ) : null}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ position: "relative" }}>
      {open && <StreamTestPanel cameraId={cameraId} />}
    </DialogContent>
  </Dialog>
);

export const StreamTestButton = ({
  available,
  cameraId,
  cameraName,
  size = "sm",
  variant = "outline",
}: {
  /** The site About's `stream_test_available`; anything but true hides it. */
  available: boolean | null | undefined;
  cameraId: number;
  cameraName?: string | null;
  size?: "sm" | "md";
  variant?: "outline" | "quiet";
}) => {
  const [open, setOpen] = React.useState(false);
  if (available !== true || !Number.isFinite(cameraId)) return null;
  return (
    <>
      <TsButton
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        data-testid={`stream-test-button-${cameraId}`}
      >
        Test stream
      </TsButton>
      <StreamTestDialog
        open={open}
        onClose={() => setOpen(false)}
        cameraId={cameraId}
        cameraName={cameraName}
      />
    </>
  );
};
